import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { DragDropModule } from '@angular/cdk/drag-drop';

import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbDateAdapter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { InlineSVGModule } from 'ng-inline-svg-2';

import { AccountsComponent } from './accounts/accounts.component';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { AuthService } from './auth.service';
import { AuthGuard } from './auth-guard.service';
import { BaseComponent } from './base/base.component';
import { OrganizationService } from './organization.service';
import { OrganizationsComponent } from './organizations/organizations.component';
import { OrganizationDetailComponent } from './organization-detail/organization-detail.component';
import { OrgUsageReportComponent } from './org-usage-report/org-usage-report.component';
import { ClassDetailComponent } from './class-detail/class-detail.component';
import { DataService } from './data/data.service';
import { DateAdapterService } from './date-adapter-service';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { LoadingSpinnerComponent } from './loading-spinner/loading-spinner.component';
import { LoginComponent } from './login/login.component';
import { LogoutComponent } from './logout/logout.component';
import { NavComponent } from './nav/nav.component';
import { ProfilePictureComponent } from './profile-picture/profile-picture.component';
import { RedirectService } from './redirect.service';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { StudentDetailComponent } from './student-detail/student-detail.component';
import { AccountDetailComponent } from './account-detail/account-detail.component';
import { CenteredPageWrapperComponent } from './centered-page-wrapper/centered-page-wrapper.component';
import { PasscodeComponent } from './passcode/passcode.component';
import { StudentsPasscodeComponent } from './students-passcode/students-passcode.component';
import { ClassReportComponent } from './class-report/class-report.component';
import { UnitReportComponent } from './unit-report/unit-report.component';
import { PendingChangesGuard } from './pending-changes';
import { SortToggleComponent } from './sort-toggle/sort-toggle.component';
import { CsvButtonsComponent } from './csv-buttons/csv-buttons.component';
import { OrgListComponent } from './org-list/org-list.component';
import { PageWrapperComponent } from './page-wrapper/page-wrapper.component';
import { OrgAdminsListComponent } from './org-admins-list/org-admins-list.component';
import { DatepickerComponent } from './datepicker/datepicker.component';
import { AddOrEditUserModal } from './add-or-edit-user-modal/add-or-edit-user-modal.component';
import { AddOrEditOrgModalComponent } from './add-or-edit-org-modal/add-or-edit-org-modal.component';
import { AddOrEditClassModalComponent } from './add-or-edit-class-modal/add-or-edit-class-modal.component';
import { AddStudentModalComponent } from './add-student-modal/add-student-modal.component';
import { RecentArtifactsComponent } from './recent-artifacts/recent-artifacts.component';
import { ClassTeachersListComponent } from './class-teachers-list/class-teachers-list.component';
import { ClassListComponent } from './class-list/class-list.component';
import { ClassUsageLimitsComponent } from './class-usage-limits/class-usage-limits.component';
import { ParentAccountDetailComponent } from './parent-account-detail/parent-account-detail.component';
import { AddParentAccountModalComponent } from './add-parent-account-modal/add-parent-account-modal.component';
import { AssignStudentToParentModalComponent } from './assign-student-to-parent-modal/assign-student-to-parent-modal.component';
import { StudentProgressReportComponent } from './student-progress-report/student-progress-report.component';
import { StudentProgressReportKinderComponent } from './student-progress-report-kinder/student-progress-report-kinder.component';
import { StudentProgressReportPathwaysComponent } from './student-progress-report-pathways/student-progress-report-pathways.component';
import { StudentProgressReportStrandComponent } from './student-progress-report-strand/student-progress-report-strand.component';
import { ActivityResultsComponent } from './activity-results/activity-results.component';
import { EncounterDisplayVocabSayComponent } from './encounter-display-vocab-say/encounter-display-vocab-say.component';
import { AudioArtifactComponent } from './audio-artifact/audio-artifact.component';
import { SpeechRecArtifactComponent } from './speech-rec-artifact/speech-rec-artifact.component';
import { ArtifactDisplayComponent } from './artifact-display/artifact-display.component';
import { ArtifactDisplaySpeechRecComponent } from './artifact-display-speech-rec/artifact-display-speech-rec.component';
import { CertificateDisplayComponent } from './certificate-display/certificate-display.component';
import { ClassAssignmentComponent } from './class-assignment/class-assignment.component';
import { ClassAssessmentsComponent } from './class-assessments/class-assessments.component';
import { DropdownStandardComponent } from './dropdown-standard/dropdown-standard.component';
import { ClassAssignmentReportComponent } from './class-assignment-report/class-assignment-report.component';
import { VersionNumberComponent } from './version-number/version-number.component';
import { CertificateRendererComponent } from './certificate-renderer/certificate-renderer.component';
import { EmailCertificateModalComponent } from './email-certificate-modal/email-certificate-modal.component';
import { AddOrEditAssignmentModalComponent } from './add-or-edit-assignment-modal/add-or-edit-assignment-modal.component';
import { RequestAssessmentModalComponent } from './request-assessment-modal/request-assessment-modal.component';
import { ClassAssessmentDetailsModalComponent } from './class-assessment-details-modal/class-assessment-details-modal.component';
import { ClassAssignmentDetailsComponent } from './class-assignment-details/class-assignment-details.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AssignmentDatePickerComponent } from './assignment-date-picker/assignment-date-picker.component';
import { ClassStudentsReportComponent } from './class-students-report/class-students-report.component';
import { EeuReportModalComponent } from './eeu-report-modal/eeu-report-modal.component';
import { CreateCustomerAccountComponent } from './create-customer-account/create-customer-account.component';
import { OrgOnboardingComponent } from './org-onboarding/org-onboarding.component';
import { AssignOnboardingModalComponent } from './assign-onboarding-modal/assign-onboarding-modal.component';
import { OrgImportComponent } from './org-import/org-import.component';
import { OnboardingBannerComponent } from './onboarding-banner/onboarding-banner.component';
import { DragFileDirective } from './drag-file.directive';
import { CleverRosteringComponent } from './clever-rostering/clever-rostering.component';
import { OnboardingNotificationModalComponent } from './onboarding-notification-modal/onboarding-notification-modal.component';
import { EmailSupportModalComponent } from './email-support-modal/email-support-modal.component';
import { DaisychainConversationsViewerComponent } from './daisychain-conversations-viewer/daisychain-conversations-viewer.component';
import { DaisychainResponseComponent } from './daisychain-response/daisychain-response.component';
import { VjsPlayerComponent } from './vjs-player/vjs-player.component';
import { DateInterceptor } from './http-interceptors/date-interceptor';
import { StudentsComponent } from './students/students.component';
import { ClassSummaryComponent } from './class-summary/class-summary.component';
import { ClassWeeklyReportComponent } from './class-weekly-report/class-weekly-report.component';
import { ClassEeuReportComponent } from './class-eeu-report/class-eeu-report.component';
import { QRCodeModule } from 'angularx-qrcode';
import { PrintQrCodesComponent } from './print-qr-codes/print-qr-codes.component';
import { EmailQrModalComponent } from './email-qr-modal/email-qr-modal.component';
import { StudentQrModalComponent } from './student-qr-modal/student-qr-modal.component';
import { StudentQrModalContentComponent } from './student-qr-modal-content/student-qr-modal-content.component';
import { KadychainConversationsViewerComponent } from './kadychain-conversations-viewer/kadychain-conversations-viewer.component';
import { KadychainChatViewerComponent } from './kadychain-chat-viewer/kadychain-chat-viewer.component';
import { TeacherMenuLinkComponent } from './teacher-menu-link/teacher-menu-link.component';
import { SpeakingScoreMeterComponent } from './speaking-score-meter/speaking-score-meter.component';
import { OrganizationDataComponent } from './organization-data/organization-data.component';
import { OrganizationClassesDataComponent } from './organization-classes-data/organization-classes-data.component';
import { SupportToolboxComponent } from './support-toolbox/support-toolbox/support-toolbox.component';
import { LicenseTrackingComponent } from './license-tracking/license-tracking.component';
import { TopSchoolsReportComponent } from './support-toolbox/support-tools/top-schools-report/top-schools-report.component';
import { ReportWithDatesIncludeNoActivityOldComponent } from './support-toolbox/support-tools/report-with-dates-include-no-activity-old/report-with-dates-include-no-activity-old.component';
import { GenericReportComponent } from './support-toolbox/support-tools/generic-report/generic-report.component';
import { AdminAccountsReportComponent } from './support-toolbox/support-tools/admin-accounts-report/admin-accounts-report.component';
import { ScrubOrgComponent } from './support-toolbox/support-tools/scrub-org/scrub-org.component';
import { OrganizationLicenseSummaryComponent } from './organization-license-summary/organization-license-summary.component';
import { OrgLicenseDetailsComponent } from './org-license-details/org-license-details.component';
import { AddOrEditLicensesModalComponent } from './add-or-edit-licenses-modal/add-or-edit-licenses-modal.component';
import { LicenseChangeAnalysisModalComponent } from './license-change-analysis-modal/license-change-analysis-modal.component';
import { ReportWithDatesComponent } from './support-toolbox/support-tools/report-with-dates/report-with-dates.component';
import { WidaWidgetComponent } from './wida-widget/wida-widget.component';
import { StudentSparcDetailsComponent } from './student-sparc-details/student-sparc-details.component';
import { StaffAccountsReportComponent } from './support-toolbox/support-tools/staff-accounts-report/staff-accounts-report.component';
import { AggregateLicenseReportComponent } from './support-toolbox/support-tools/aggregate-license-report/aggregate-license-report.component';
import { StudentDomainScoresComponent } from './student-domain-scores/student-domain-scores.component';
import { StrandUnitReportComponent } from './strand-unit-report/strand-unit-report.component';

@NgModule({
  declarations: [
    AccountsComponent,
    AppComponent,
    BaseComponent,
    OrganizationsComponent,
    OrganizationDetailComponent,
    OrgUsageReportComponent,
    ClassDetailComponent,
    ForgotPasswordComponent,
    LoadingSpinnerComponent,
    LoginComponent,
    LogoutComponent,
    NavComponent,
    ProfilePictureComponent,
    ResetPasswordComponent,
    StudentDetailComponent,
    AccountDetailComponent,
    CenteredPageWrapperComponent,
    PasscodeComponent,
    StudentsPasscodeComponent,
    ClassReportComponent,
    UnitReportComponent,
    SortToggleComponent,
    CsvButtonsComponent,
    OrgListComponent,
    PageWrapperComponent,
    OrgAdminsListComponent,
    DatepickerComponent,
    AddOrEditUserModal,
    AddOrEditOrgModalComponent,
    AddOrEditClassModalComponent,
    AddStudentModalComponent,
    RecentArtifactsComponent,
    ClassTeachersListComponent,
    ClassListComponent,
    ClassUsageLimitsComponent,
    ParentAccountDetailComponent,
    AddParentAccountModalComponent,
    AssignStudentToParentModalComponent,
    StudentProgressReportComponent,
    StudentProgressReportKinderComponent,
    StudentProgressReportPathwaysComponent,
    StudentProgressReportStrandComponent,
    StudentSparcDetailsComponent,
    WidaWidgetComponent,
    ActivityResultsComponent,
    EncounterDisplayVocabSayComponent,
    AudioArtifactComponent,
    SpeechRecArtifactComponent,
    ArtifactDisplayComponent,
    ArtifactDisplaySpeechRecComponent,
    CertificateDisplayComponent,
    ClassAssignmentComponent,
    ClassAssessmentsComponent,
    DropdownStandardComponent,
    ClassAssignmentReportComponent,
    VersionNumberComponent,
    CertificateRendererComponent,
    EmailCertificateModalComponent,
    AddOrEditAssignmentModalComponent,
    RequestAssessmentModalComponent,
    ClassAssessmentDetailsModalComponent,
    ClassAssignmentDetailsComponent,
    AssignmentDatePickerComponent,
    ClassStudentsReportComponent,
    EeuReportModalComponent,
    CreateCustomerAccountComponent,
    OrgOnboardingComponent,
    AssignOnboardingModalComponent,
    OrgImportComponent,
    OnboardingBannerComponent,
    DragFileDirective,
    CleverRosteringComponent,
    OnboardingNotificationModalComponent,
    EmailSupportModalComponent,
    DaisychainConversationsViewerComponent,
    DaisychainResponseComponent,
    KadychainConversationsViewerComponent,
    KadychainChatViewerComponent,
    VjsPlayerComponent,
    StudentsComponent,
    ClassSummaryComponent,
    ClassWeeklyReportComponent,
    ClassEeuReportComponent,
    PrintQrCodesComponent,
    EmailQrModalComponent,
    StudentQrModalComponent,
    StudentQrModalContentComponent,
    TeacherMenuLinkComponent,
    SpeakingScoreMeterComponent,
    OrganizationDataComponent,
    OrganizationClassesDataComponent,
    SupportToolboxComponent,
    LicenseTrackingComponent,
    TopSchoolsReportComponent,
    ReportWithDatesIncludeNoActivityOldComponent,
    AggregateLicenseReportComponent,
    ReportWithDatesComponent,
    GenericReportComponent,
    AdminAccountsReportComponent,
    StaffAccountsReportComponent,
    ScrubOrgComponent,
    OrganizationLicenseSummaryComponent,
    OrgLicenseDetailsComponent,
    AddOrEditLicensesModalComponent,
    LicenseChangeAnalysisModalComponent,
    StudentDomainScoresComponent,
    StrandUnitReportComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    HttpClientModule,
    InlineSVGModule,
    NgbModule,
    BrowserAnimationsModule,
    DragDropModule,
    QRCodeModule
  ],
  providers: [
    AuthService,
    AuthGuard,
    DataService,
    OrganizationService,
    PendingChangesGuard,
    RedirectService,
    { provide: NgbDateAdapter, useClass: DateAdapterService },
    { provide: HTTP_INTERCEPTORS, useClass: DateInterceptor, multi: true }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
